import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DrawerWorkorderTitleComponent } from '../drawers/drawer-workorder-title/drawer-workorder-title.component';
import { MatIconModule } from '@angular/material/icon';
import { MTX_DRAWER_DATA, MtxDrawerRef } from '@ng-matero/extensions/drawer';
import { EmployeesRepository } from 'src/employees/employees.repository';
import { EmployeeVisitsRepository } from 'src/employee-visits/employeeVisits.repository';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';
import { EmployeesQuery } from 'src/employees/state/employees.query';
import { EmployeeVisitsQuery } from 'src/employee-visits/state/employeeVisits.query';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { WorkOrdersRepository } from '../workorders.repository';
import { ConfirmDialogComponent } from 'src/app/shared/dialogs/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { WorkorderSignatureWizardComponent } from '../workorder-signature-wizard/workorder-signature-wizard.component';
import { DrawerWorkorderActionsComponent } from '../drawers/drawer-workorder-actions/drawer-workorder-actions.component';
import { EmployeeVisitsByDay } from './employee-visits-by-day';
import { EmployeeVisit } from './employee-visit';
import { EmployeeVisitsView } from './employee-visits-view-enum';
import { CreateEmployeeVisitsWizardComponent } from './create-employee-visits-wizard/create-employee-visits-wizard.component';
import { EditEmployeeVisitsWizardComponent } from './edit-employee-visits-wizard/edit-employee-visits-wizard.component';
import { EmployeeVisitsService } from 'src/employee-visits/employee-visits.service';
import { TranslateModule } from '@ngx-translate/core';
import { WorkOrderTranslateService } from 'src/app/shared/services/translate-workorder.service';
import { LocalizedDatePipe } from 'src/app/shared/localizedDatePipe';

@Component({
  selector: 'app-workorder-employee-visits',
  standalone: true,
  templateUrl: './workorder-employee-visits.component.html',
  styleUrls: ['./workorder-employee-visits.component.scss'],
  imports: [
    CommonModule,
    DrawerWorkorderTitleComponent,
    MatIconModule,
    MatButtonModule,
    WorkorderSignatureWizardComponent,
    DrawerWorkorderActionsComponent,
    CreateEmployeeVisitsWizardComponent,
    EditEmployeeVisitsWizardComponent,
    TranslateModule,
    LocalizedDatePipe,
  ],
})
export class WorkorderEmployeeVisitsComponent implements OnInit {
  @ViewChild('createEmployeeVisitsWizard') createEmployeeVisitsWizard!: CreateEmployeeVisitsWizardComponent;
  @ViewChild('editEmployeeVisitsWizard') editEmployeeVisitsWizard!: EditEmployeeVisitsWizardComponent;
  @ViewChild('signatureWizard') signatureWizard!: WorkorderSignatureWizardComponent;

  employeeVisitsByDay: BehaviorSubject<EmployeeVisitsByDay[]> = new BehaviorSubject<EmployeeVisitsByDay[]>([]);
  employeeVisitsByDay$: Observable<EmployeeVisitsByDay[]> = this.employeeVisitsByDay.asObservable();

  visitsView: BehaviorSubject<EmployeeVisitsView> = new BehaviorSubject<EmployeeVisitsView>(EmployeeVisitsView.List);
  visitsView$: Observable<EmployeeVisitsView> = this.visitsView.asObservable();

  EmployeeVisitsView = EmployeeVisitsView;
  currentView: EmployeeVisitsView = EmployeeVisitsView.List;
  drawerTitle = '';

  employeeVisitsToEdit: EmployeeVisit[] = [];
  saveButtonIsEnable = true;
  confirmButtonName = '';

  get workOrder() {
    return this.data.workOrder;
  }

  constructor(
    public drawerRef: MtxDrawerRef<WorkorderEmployeeVisitsComponent>,
    @Inject(MTX_DRAWER_DATA) public data: any,
    private employeesRepository: EmployeesRepository,
    private employeesQuery: EmployeesQuery,
    private employeeVisitsRepository: EmployeeVisitsRepository,
    private employeeVisitsQuery: EmployeeVisitsQuery,
    private employeeVisitsService: EmployeeVisitsService,
    private workOrdersRepository: WorkOrdersRepository,
    private dialog: MatDialog,
    private workorderTranslateService: WorkOrderTranslateService,
    private datePipe: LocalizedDatePipe
  ) {
    combineLatest([
      this.employeesQuery.selectAll(),
      this.employeeVisitsQuery.selectAll({
        filterBy: (employeeVisit) => employeeVisit.workOrderId == this.workOrder.id,
      }),
    ])
      .pipe(
        takeUntilDestroyed(),
        map(([employees, employeeVisits]) => {
          const visitsByStartDate = employeeVisits.reduce(function (groups: any, employeeVisit) {
            const startDate = employeeVisit.startDate!.split('T')[0];
            if (!groups[startDate]) {
              groups[startDate] = [];
            }
            groups[startDate].push(employeeVisit);
            return groups;
          }, {});

          const employeeVisitsByDay = Object.keys(visitsByStartDate)
            .map((date) => {
              let employeeVisitsByDay = this.employeeVisitsService.getVisitsByDay(visitsByStartDate[date], employees);

              return {
                day: new Date(date!),
                employeeNames: this.employeeVisitsService.getEmployeeDisplayNames([
                  ...new Set(
                    employeeVisitsByDay.map((ev) => {
                      return ev.employeeAbbreviatedName;
                    })
                  ),
                ]),
                employeeVisitsByDay: employeeVisitsByDay,
                totalHours: this.employeeVisitsService.getTotalHoursForDay(employeeVisitsByDay),
              } as EmployeeVisitsByDay;
            })
            .sort((a, b) => new Date(b.day!).getTime() - new Date(a.day!).getTime());

          this.employeeVisitsByDay.next(employeeVisitsByDay);
          this.saveButtonIsEnable = employeeVisits.length > 0;
        })
      )
      .subscribe();

    //employee visits view
    this.visitsView$
      .pipe(
        map((view) => {
          this.currentView = view;
          this.changeDrawerTitleAndButtons();
        })
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.employeesRepository.loadEmployees();
    this.employeeVisitsRepository.loadWorkOrderEmployeeVisits(this.workOrder.id as number);
    if (this.workOrder.workOrderState == 'Assigned') {
      this.changeEmployeeVisitsView(EmployeeVisitsView.CreateWizard);
    }
  }

  openCreateEmployeeVisitsWizard() {
    this.saveButtonIsEnable = false;
    this.changeEmployeeVisitsView(EmployeeVisitsView.CreateWizard);
  }

  openEditEmployeeVisitsWizard(employeeVisits: EmployeeVisit[]) {
    this.employeeVisitsToEdit = employeeVisits;
    this.changeEmployeeVisitsView(EmployeeVisitsView.EditWizard);
  }

  confirm() {
    switch (this.currentView) {
      case EmployeeVisitsView.List:
        this.showSignatureWizard();
        break;
      case EmployeeVisitsView.CreateWizard:
        this.createEmployeeVisits();
        break;
      case EmployeeVisitsView.EditWizard:
        this.editEmployeeVisits();
        break;
      case EmployeeVisitsView.SignatureWizard:
        this.finishWorkOrder();
        break;
      default:
        break;
    }
  }

  createEmployeeVisits() {
    this.createEmployeeVisitsWizard.createEmployeeVisits();
  }

  editEmployeeVisits() {
    this.editEmployeeVisitsWizard.saveEmployeeVisits();
  }

  showSignatureWizard() {
    this.saveButtonIsEnable = false;
    this.changeEmployeeVisitsView(EmployeeVisitsView.SignatureWizard);
  }

  finishWorkOrder() {
    if (this.signatureWizard) {
      const employeeSignature = this.signatureWizard.exportEmployeeSignature();
      const clientSignature = this.signatureWizard.exportClientSignature();
      const attachedFiles = this.signatureWizard.getAttachedFiles();
      const observations = this.signatureWizard.getObservations();
      const clientSigner = this.signatureWizard.getClientSignerName();

      this.workOrdersRepository.finishWorkOrder(
        this.workOrder.id,
        employeeSignature,
        clientSignature,
        attachedFiles,
        observations!,
        clientSigner!
      );

      this.drawerRef.dismiss();
    }
  }

  enableSaveButton(enable: boolean) {
    this.saveButtonIsEnable = enable;
  }

  cancel() {
    this.saveButtonIsEnable = true;
    this.currentView == EmployeeVisitsView.List ||
    (this.workOrder.workOrderState == 'Assigned' && this.currentView == EmployeeVisitsView.CreateWizard)
      ? this.drawerRef.dismiss()
      : this.changeEmployeeVisitsView(EmployeeVisitsView.List);
  }

  deleteEmployeeVisits(day: EmployeeVisitsByDay) {
    if (day) {
      const removeMsg = this.workorderTranslateService.translateRemoveVisitsMessage();
      const dayToDelete = this.datePipe.transform(day.day, 'dd MMMM, yyyy');

      let dialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: 'auto',
        data: { confirm: false, msg: removeMsg, displayInfo: dayToDelete },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          const employeeVisitsToRemove = day.employeeVisitsByDay?.map((ev) => ev.id);
          this.workOrdersRepository.removeWorkOrderEmployeeVisits(
            this.workOrder.id,
            employeeVisitsToRemove as number[]
          );
        }
      });
    }
  }

  changeEmployeeVisitsView(visitsView: EmployeeVisitsView) {
    if (visitsView) {
      this.visitsView.next(visitsView);
    }
  }

  private changeDrawerTitleAndButtons() {
    let title = '';
    let buttonName = '';
    switch (this.currentView) {
      case EmployeeVisitsView.List:
        title = 'Manage Visits';
        buttonName = 'Finish';
        break;
      case EmployeeVisitsView.CreateWizard:
        title = 'Create Visit';
        buttonName = 'Save';
        break;
      case EmployeeVisitsView.EditWizard:
        title = 'Edit Visit';
        buttonName = 'Save';
        break;
      case EmployeeVisitsView.SignatureWizard:
        title = 'Finish Visit';
        buttonName = 'Finish';
        break;
      default:
        break;
    }

    this.drawerTitle = this.workorderTranslateService.translateDrawerTitle(title);
    this.confirmButtonName = this.workorderTranslateService.translateDrawerConfirmButton(buttonName);
  }
}
